/**
 * <reward-stepper> — tiered cart progress toward free shipping and any gift.
 *
 * Liquid paints the first frame (see reward-stepper.liquid) so the bar is
 * already correct before this runs; this element keeps it in step with the cart
 * afterwards. It never decides anything the gift engine also decides — it reads
 * the same post-discount, gift-excluded subtotal from reward-subtotal.js.
 *
 * Deliberate behaviours, each of which is a bug if you get it the other way:
 *
 *   - Tiers are sorted by amount, not by config order. A future promo may put
 *     the gift below the shipping threshold.
 *   - The cart node reads LOCKED at exactly 0. An empty cart (or one holding
 *     only an auto-added gift) must not show a completed step.
 *   - Nodes are laid out with flex, never percentages. Percentage-positioned
 *     labels clip at the rail ends.
 *   - The rail fill scales to the TOP tier, so it keeps advancing past the
 *     first threshold instead of resetting.
 *   - aria-live sits on the message only, never the rail — one announcement per
 *     unlock, not one per percent.
 */
(function () {

  /**
   * Store-currency money from the shop's own money format, e.g. "${{amount}}"
   * or "&euro;{{amount}}" (Shopify hands the symbol over HTML-encoded). The
   * theme has no Shopify.formatMoney, which is why this exists: the old
   * fallback hard-coded "$", so EU and UK showed dollars. Whole amounts drop
   * their decimals ("€30", not "€30.00") to keep the reward lines short.
   */
  function fbMoney(major, format) {
    var fmt = String(format || "${{amount}}");
    if (fmt.indexOf("&") !== -1) {
      var t = document.createElement("textarea");
      t.innerHTML = fmt;
      fmt = t.value;
    }
    var whole = Math.abs(major - Math.round(major)) < 0.005;
    function group(n, decimals, thousands, point) {
      var parts = Math.abs(n).toFixed(decimals).split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return (n < 0 ? "-" : "") + parts.join(point);
    }
    var styles = {
      amount: [",", "."],
      amount_no_decimals: [",", "."],
      amount_with_comma_separator: [".", ","],
      amount_no_decimals_with_comma_separator: [".", ","],
      amount_with_apostrophe_separator: ["'", "."],
      amount_with_space_separator: [" ", ","],
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
      var s = styles[key] || styles.amount;
      var decimals = whole || key.indexOf("no_decimals") !== -1 ? 0 : 2;
      return group(major, decimals, s[0], s[1]);
    });
  }
  // Remembered across element instances. The theme re-renders the whole cart
  // section on every quantity change, destroying and recreating this element,
  // so a fresh one has no idea where the bar was a moment ago. Without this the
  // fill is created at 0% and given its final width in the same frame — no
  // starting value, so the browser has nothing to animate from and it snaps.
  //
  // Keyed BY SURFACE, not global: on /cart the drawer and the page each render
  // their own stepper. With one shared number the first to repaint would eat
  // the "a tier just unlocked" transition and the other would snap, and which
  // of the two animated would be whichever happened to paint first — usually
  // the one nobody is looking at.
  // Long enough to cover the whole unlock sequence: 300ms for the fill to
  // arrive, then the pop, ring and tick behind it.
  const CELEBRATION_MS = 1100;

  const memory = {};
  function remember(key) {
    if (!memory[key]) memory[key] = { pct: null, unlocked: null, celebrate: null };
    return memory[key];
  }

  /**
   * Forget where the bar was.
   *
   * An empty cart renders no stepper at all, so emptying the cart leaves the
   * remembered width sitting at whatever it was — delete the last line from a
   * full bar, add something back, and the new element plants a FULL rail for a
   * frame before dropping to the real one. Decreasing the quantity does not do
   * this, because the bar never goes away.
   *
   * No stepper on the page means there is nothing to animate from.
   */
  function forget() {
    Object.keys(memory).forEach((k) => {
      memory[k] = { pct: null, unlocked: null, celebrate: null };
    });
  }

  function forgetIfNoBar() {
    // After the event, not during it: the drawer publishes before the new
    // markup has been put in place.
    requestAnimationFrame(() => {
      // .fb-reward__fill, not [data-reward-fill]: the announcement bar's
      // progress strip carries that attribute too, and it is on every page —
      // so the check never saw a cart without a bar.
      if (!document.querySelector(".fb-reward__fill")) forget();
    });
  }

  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, forgetIfNoBar);
  }
  document.addEventListener("cart-drawer:refresh", forgetIfNoBar);
  // And on arrival: a page loaded with an empty cart starts from nothing.
  forgetIfNoBar();

  const ICONS = {
    bag: '<path d="M6 8h12l-1 12H7z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>',
    truck:
      '<path d="M2 7h11v9H2z"/><path d="M13 10h4l4 3v3h-8z"/><circle cx="6.5" cy="18" r="1.8"/><circle cx="17" cy="18" r="1.8"/>',
    tick: '<path class="fb-reward__tickpath" d="M5 12.5l4.5 4.5L19 7.5"/>',
    gift: '<path d="M3 10h18v10H3z"/><path d="M3 7h18v3H3z"/><path d="M12 7v13"/><path d="M12 7C10 7 8 6 8 4.5S10 3 12 7zM12 7c2 0 4-1 4-2.5S14 3 12 7z"/>',
  };

  function svg(name) {
    return (
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      (ICONS[name] || ICONS.gift) +
      "</svg>"
    );
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    })[c]);
  }

  class RewardStepper extends HTMLElement {
    connectedCallback() {
      try {
        this.tiers = JSON.parse(this.dataset.tiers || "[]");
      } catch (e) {
        this.tiers = [];
      }
      // Sorted once here rather than at every paint — a future promo may place
      // the gift below the shipping threshold, and config order is not reliable.
      this.tiers = this.tiers
        .filter((t) => t && Number(t.amount) > 0)
        .sort((a, b) => Number(a.amount) - Number(b.amount));

      // Two tiers on the same amount would otherwise paint two dots on top of
      // each other. Collapse them into one node carrying both labels.
      this.tiers = this.tiers.reduce((acc, t) => {
        const prev = acc[acc.length - 1];
        if (prev && Number(prev.amount) === Number(t.amount)) {
          prev.label = [prev.label, t.label].filter(Boolean).join(" + ");
          // Keep the first tier's image/icon; the combined node can only show one.
          prev.image = prev.image || t.image;
          return acc;
        }
        acc.push(Object.assign({}, t));
        return acc;
      }, []);

      this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
      this.mobileLayout = this.dataset.mobileLayout || "auto";
      // One breakpoint, shared with the stylesheet. The vertical list is a
      // different set of rows rather than the same nodes restyled, so CSS alone
      // cannot switch to it — this element has to know the width too.
      this.mq = window.matchMedia("(max-width: 600px)");
      this.onBreakpoint = () => {
        this.lastNodesHtml = null;
        if (typeof this.lastSubtotal === "number") this.paint(this.lastSubtotal);
      };
      if (this.mq.addEventListener) this.mq.addEventListener("change", this.onBreakpoint);
      this.emptyMessage = this.dataset.emptyMessage || "";
      // Which surface this instance is — drawer or cart page — so the two keep
      // separate memories of where the bar was.
      const section = this.closest('[id^="shopify-section"]');
      this.memory = remember(section ? section.id : "default");

      this.msgEl = this.querySelector("[data-reward-message]");
      this.fillEl = this.querySelector("[data-reward-fill]");
      this.nodesEl = this.querySelector("[data-reward-nodes]");
      this.railEl = this.querySelector('[role="progressbar"]');

      this.paint((Number(this.dataset.subtotal) || 0) / 100);

      // Repaint whenever anything publishes a cart change — the same signal the
      // gift engine listens to, so the two can't fall out of step.
      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
      this.onDrawerRefresh = () => this.refresh();
      document.addEventListener("cart-drawer:refresh", this.onDrawerRefresh);

      // The dots move when the box does — a rotated phone, or the breakpoint
      // that drops the labels. Re-measure rather than leave the fill pointing
      // at where a dot used to be.
      this.onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          if (typeof this.lastSubtotal === "number") this.paint(this.lastSubtotal);
        }, 120);
      };
      window.addEventListener("resize", this.onResize);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      document.removeEventListener("cart-drawer:refresh", this.onDrawerRefresh);
      window.removeEventListener("resize", this.onResize);
      if (this.mq && this.mq.removeEventListener) {
        this.mq.removeEventListener("change", this.onBreakpoint);
      }
      clearTimeout(this.resizeTimer);
    }

    async refresh() {
      let cart;
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        cart = await fetch(`${root}cart.js`).then((r) => r.json());
      } catch (e) {
        return;
      }
      const major =
        window.FBReward && window.FBReward.subtotalMajor
          ? window.FBReward.subtotalMajor(cart)
          : // Fallback mirrors reward-subtotal.js so a missing asset degrades to
            // a still-correct bar rather than a frozen one.
            (cart.items || []).reduce(
              (sum, i) =>
                i.properties && i.properties._gift === "true"
                  ? sum
                  : sum + (i.final_line_price || 0),
              0,
            ) / 100;
      this.paint(major);
    }

    money(major) {
      return fbMoney(major, this.moneyFormat);
    }

    /** Shortfall to the next unmet tier, or null when everything is unlocked. */
    nextTier(subtotal) {
      return this.tiers.find((t) => subtotal < Number(t.amount)) || null;
    }

    message(subtotal, next) {
      // Nothing in the cart yet: say so, rather than quoting a shortfall from
      // zero. The nodes still render locked so the offer is still visible.
      if (subtotal <= 0 && this.emptyMessage) return escapeHtml(this.emptyMessage);
      if (!next) {
        const last = this.tiers[this.tiers.length - 1];
        return last && last.unlocked ? last.unlocked : "";
      }
      const remaining = this.money(Number(next.amount) - subtotal);
      // The message describes the next thing to earn; any tier already passed
      // shows its unlocked copy ahead of it.
      const passed = this.tiers.filter((t) => subtotal >= Number(t.amount));
      const prefix =
        passed.length && passed[passed.length - 1].unlocked
          ? `<span class="fb-reward__done">${escapeHtml(passed[passed.length - 1].unlocked)}</span> `
          : "";
      const body = (next.pending || "").includes("{remaining}")
        ? next.pending.replace("{remaining}", `<b>${escapeHtml(remaining)}</b>`)
        : `${escapeHtml(next.pending || "")} <b>${escapeHtml(remaining)}</b>`;
      return prefix + body;
    }

    /** Is the vertical list the right thing right now? */
    isVertical() {
      return this.mobileLayout === "vertical" && this.mq && this.mq.matches;
    }

    /**
     * One row per reward — Laura's Mobile B. Nothing truncates, and it scales
     * to any number of rewards at the cost of height.
     *
     * No cart row: on a horizontal stepper the cart is the left end of the
     * line, but as a list item "Your cart" is not a reward and reads as one.
     * The subtotal is already in the sentence above.
     */
    row(t, subtotal, nextHit) {
      const amount = Number(t.amount);
      const hit = subtotal >= amount;
      const inner = t.image
        ? `<img src="${escapeHtml(t.image)}" alt="" loading="lazy" width="30" height="30">` +
          (hit ? `<span class="fb-reward__tickover">${svg("tick")}</span>` : "")
        : svg(hit ? "tick" : t.icon);
      // A fixed word, not the tier's own copy: the row already says which
      // reward this is, and "Free shipping unlocked" beside "Free shipping"
      // says it twice and pushes the row to two lines.
      const state = hit
        ? `<span class="fb-reward__rowstate is-hit">&#10003; Unlocked</span>`
        : `<span class="fb-reward__rowstate">${escapeHtml(
            this.money(amount - subtotal),
          )} to go</span>`;
      const justUnlocked =
        this.celebrateIndex >= 0 && this.tiers[this.celebrateIndex] === t;
      return (
        `<li class="fb-reward__row${hit ? " is-hit" : ""}${
          nextHit ? " has-next-hit" : ""
        }">` +
        `<span class="fb-reward__dot${hit ? " is-hit" : ""}${
          justUnlocked ? " is-just-unlocked" : ""
        }">${inner}</span>` +
        `<span class="fb-reward__rowtext">` +
        `<span class="fb-reward__rowname">${escapeHtml(t.label)}</span>` +
        `<span class="fb-reward__rowfrom">from ${escapeHtml(this.money(amount))}</span>` +
        `</span>` +
        state +
        `</li>`
      );
    }

    node(opts) {
      const stateClass =
        (opts.hit ? " is-hit" : "") +
        (opts.next ? " is-next" : "") +
        (opts.justUnlocked ? " is-just-unlocked" : "");
      // An earned REWARD shows a tick: the icon said what was on offer, the
      // tick says it is yours. The cart node is not a reward — it keeps its bag
      // whatever the subtotal, or an empty-ish cart looks like an achievement.
      // A node carrying a product image keeps the image and takes the tick over
      // a scrim, so the gift stays recognisable.
      const earned = opts.hit && opts.reward;
      const inner = opts.image
        ? `<img src="${escapeHtml(opts.image)}" alt="" loading="lazy" width="30" height="30">` +
          (earned ? `<span class="fb-reward__tickover">${svg("tick")}</span>` : "")
        : svg(earned ? "tick" : opts.icon);
      const amt = opts.amount
        ? `<span class="fb-reward__amt">${escapeHtml(opts.amount)}</span>`
        : "";
      return (
        `<div class="fb-reward__node">` +
        `<span class="fb-reward__dot${stateClass}">${inner}</span>` +
        `<span class="fb-reward__lbl${opts.hit ? " is-hit" : ""}">` +
        // The name is its own element so the compact phone layout can drop it
        // and keep the amount.
        `<span class="fb-reward__name">${escapeHtml(opts.label)}</span>${amt}` +
        `</span>` +
        `</div>`
      );
    }

    /**
     * How far along the rail the fill should reach, measured from the dots
     * themselves rather than assumed.
     *
     * Even fractions are only correct while there are three nodes. Past that
     * the end nodes align their dot to the outside of their cell and the
     * middles centre theirs, so the dots sit at 0, 0.3, 0.5, 0.7, 1 — and a
     * fill drawn at 0.25 misses the dot at 0.3 by a visible margin. That is the
     * same defect as the original 75%-vs-50% report, returning the moment a
     * third reward is configured.
     *
     * Weights that would fix it in CSS depend on the dot's own width, so they
     * stop being right at another dot size. Measuring cannot be wrong.
     *
     * @param {number} evenFallback - the even-fraction answer, used when there
     *   is nothing to measure (hidden drawer, zero-width rail).
     */
    railPct(subtotal, evenFallback) {
      if (!this.railEl || !this.nodesEl) return evenFallback;
      const railBox = this.railEl.getBoundingClientRect();
      if (railBox.width <= 0) return evenFallback;

      const dots = this.nodesEl.querySelectorAll(".fb-reward__dot");
      if (dots.length !== this.tiers.length + 1) return evenFallback;

      // Where each node sits, as a fraction of the rail. Index 0 is the cart.
      const at = [];
      for (let i = 0; i < dots.length; i++) {
        const box = dots[i].getBoundingClientRect();
        const centre = box.left + box.width / 2 - railBox.left;
        at.push(Math.min(Math.max(centre / railBox.width, 0), 1));
      }

      const amounts = [0].concat(this.tiers.map((t) => Number(t.amount)));
      if (subtotal >= amounts[amounts.length - 1]) return 100;

      for (let i = 1; i < amounts.length; i++) {
        if (subtotal < amounts[i]) {
          const span = amounts[i] - amounts[i - 1];
          const within = span > 0 ? (subtotal - amounts[i - 1]) / span : 0;
          return (at[i - 1] + (at[i] - at[i - 1]) * within) * 100;
        }
      }
      return 100;
    }

    /**
     * Width changes animate; the jump back to where the bar already was does
     * not. On a freshly recreated element we plant the previous percentage
     * with transitions off, force the browser to commit it, then animate to the
     * new one on the next frame.
     */
    setFill(pct) {
      const target = pct + "%";
      const start = this.memory.pct;
      // Recorded before anything is drawn. It used to be written inside the
      // requestAnimationFrame below, and a cart change that re-rendered the
      // section twice destroyed this element before that frame ran — so the
      // remembered width stayed at the old value, and the next paint planted a
      // full bar for a frame before dropping to the real one.
      this.memory.pct = pct;

      if (this.hasPainted) {
        this.fillEl.style.width = target;
        return;
      }
      this.hasPainted = true;

      // Nowhere to animate from, or nowhere to animate to.
      if (start === null || start === pct) {
        this.fillEl.style.width = target;
        return;
      }

      this.fillEl.style.transition = "none";
      this.fillEl.style.width = start + "%";
      // Reading a layout property commits that width before the transition is
      // restored, which is what makes the next change animate instead of snap.
      void this.fillEl.offsetWidth;
      this.fillEl.style.transition = "";

      requestAnimationFrame(() => {
        this.fillEl.style.width = target;
      });
    }

    /**
     * Swap the copy with a fade, and only when it actually changed — the same
     * cart change arrives twice, and replaying the fade on the second one makes
     * the sentence flicker.
     */
    setMessage(html) {
      if (!this.msgEl || html === this.lastMsgHtml) return;
      this.lastMsgHtml = html;
      this.msgEl.innerHTML = html;
      this.msgEl.classList.remove("is-swapping");
      // Commit the removal, or re-adding it in the same frame is a no-op and
      // the animation never restarts.
      void this.msgEl.offsetWidth;
      this.msgEl.classList.add("is-swapping");
    }

    paint(subtotal) {
      if (!this.tiers.length) return;

      const max = Number(this.tiers[this.tiers.length - 1].amount) || 1;
      const next = this.nextTier(subtotal);
      // Position along the rail, not share of the top tier — see positionPct.
      const pct =
        window.FBReward && window.FBReward.positionPct
          ? window.FBReward.positionPct(subtotal, this.tiers)
          : Math.min(subtotal / max, 1) * 100;

      // A tier counts as JUST unlocked only if we watched it happen. On a first
      // paint (page load, or arriving at the cart with things already in it)
      // there is nothing to celebrate — the shopper crossed that line earlier.
      const unlockedCount = this.tiers.filter((t) => subtotal >= Number(t.amount)).length;
      if (this.memory.unlocked !== null && unlockedCount > this.memory.unlocked) {
        // One cart change publishes more than one event — cartUpdate and the
        // drawer's own refresh. Each repaint re-renders the nodes, so without a
        // window here the second one strips the class 300ms in and the pop, the
        // ring and the tick are all cut off mid-flight.
        this.memory.celebrate = { index: unlockedCount - 1, until: Date.now() + CELEBRATION_MS };
      }
      this.memory.unlocked = unlockedCount;

      const celebrating =
        this.memory.celebrate && Date.now() < this.memory.celebrate.until
          ? this.memory.celebrate
          : null;
      // A tier that has since been un-earned (item removed mid-animation) must
      // not keep celebrating.
      const justUnlockedIndex =
        celebrating && celebrating.index < unlockedCount ? celebrating.index : -1;
      this.celebrateIndex = justUnlockedIndex;

      if (this.railEl) {
        this.railEl.setAttribute("aria-valuenow", String(subtotal));
        this.railEl.setAttribute("aria-valuemax", String(max));
      }

      // Cart node: locked at exactly 0, unlocked from the first cent.
      let html = this.node({
        icon: "bag",
        label: "Your cart",
        amount: this.money(subtotal),
        hit: subtotal > 0,
        next: false,
        reward: false,
      });

      html += this.tiers
        .map((t, i) =>
          this.node({
            icon: t.icon,
            image: t.image,
            label: t.label,
            amount: this.money(Number(t.amount)),
            hit: subtotal >= Number(t.amount),
            next: !!next && Number(next.amount) === Number(t.amount),
            justUnlocked: i === justUnlockedIndex,
            reward: true,
          }),
        )
        .join("");

      // Only rewrite when something actually changed. One cart change publishes
      // two events, and rewriting identical markup replaces the dot elements —
      // which restarts the unlock animation from the top, so the pop played
      // twice and the tick drew late.
      // Drives the phone layout: with four or five nodes the names are dropped
      // and the amounts kept, because four labelled nodes do not fit a phone
      // and truncating them is worse than losing them — the message above the
      // steps names the next reward anyway. A number, not a breakpoint guess,
      // and nothing for a manager to set.
      this.dataset.nodes = String(this.tiers.length + 1);
      this.dataset.mobile = this.mobileLayout;

      if (this.isVertical()) {
        html =
          '<ul class="fb-reward__rows" role="list">' +
          this.tiers
            .map((t, i) => {
              const nextTier = this.tiers[i + 1];
              return this.row(t, subtotal, !!nextTier && subtotal >= Number(nextTier.amount));
            })
            .join("") +
          "</ul>";
      }
      this.dataset.view = this.isVertical() ? "vertical" : "steps";

      if (this.nodesEl && html !== this.lastNodesHtml) {
        this.nodesEl.innerHTML = html;
        this.lastNodesHtml = html;
      }
      this.setMessage(this.message(subtotal, next));

      // After the nodes, never before: the fill is measured against where the
      // dots actually are.
      this.lastSubtotal = subtotal;
      // The vertical list has no rail; its rows carry the state themselves.
      if (this.fillEl && !this.isVertical()) this.setFill(this.railPct(subtotal, pct));
    }
  }

  if (!customElements.get("reward-stepper")) {
    customElements.define("reward-stepper", RewardStepper);
  }
})();
